/**
 * FCPX-startpakket fase 2/3: de clips van een draaidag, met de soort (interview/b-roll), de
 * transcripten en het benoemen van sprekers. Lezen mag de kring van de projectpagina; wijzigen alleen de
 * PL of een admin (job.rechten / clips.rechten, bepaald door de server).
 *
 * Vangnetten (policies/ff-design.md): de soort en sprekernamen zijn terug te zetten (zelfde keuzelijst/
 * veld); "Opnieuw transcriberen" vraagt een bevestiging omdat het een nieuwe AI-verwerking start.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { T, Icon, stitchOutlineBtn } from './stitch';
import { authFetch } from '../../../utils/authHelpers';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { bevestig } from '../../../utils/bevestig';
import { SOORT_LABELS, duurTekst } from '../../../utils/startpakket';

async function vraag(pad, opties) {
  const r = await authFetch(`${BACKEND_URL}/Api/startpakket${pad}`, opties);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || data.error || `Mislukt (${r.status})`);
  return data;
}

const post = (pad, body) => vraag(pad, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
});

const AUDIO_TEKST = {
  nodig: 'wacht op audio', ontvangen: 'audio binnen', wachtrij: 'in de wachtrij', bezig: 'wordt getranscribeerd', klaar: 'getranscribeerd',
  fout: 'transcriptie mislukt', overgeslagen: 'overgeslagen', niet_nodig: '',
};

function Transcript({ jobId, clip, beheren }) {
  const [data, setData] = useState(null);
  const [namen, setNamen] = useState({});
  const [bezig, setBezig] = useState(false);
  useEffect(() => {
    vraag(`/jobs/${jobId}/clips/${clip.id}/transcript`).then((d) => {
      setData(d);
      const n = {};
      (d.clip?.transcript?.sprekers || []).forEach((s) => { n[s.label] = s.naam || ''; });
      setNamen(n);
    }).catch((e) => toast.error(e.message));
  }, [jobId, clip.id]);
  if (!data) return <Typography variant="caption" sx={{ color: T.outline }}>Transcript laden…</Typography>;
  const sprekers = data.clip?.transcript?.sprekers || [];
  const bewaar = async () => {
    setBezig(true);
    try {
      setData(await post(`/jobs/${jobId}/clips/${clip.id}/sprekers`, { sprekers: namen }));
      toast.success('Sprekers opgeslagen — ze komen in de volgende pakketversie');
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };
  return (
    <Box sx={{ mt: 1, pl: 1.5, borderLeft: `2px solid ${T.surfaceHigh}`, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {beheren && sprekers.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {sprekers.map((s) => (
            <TextField key={s.label} size="small" label={`Spreker ${s.label}`} value={namen[s.label] || ''}
              onChange={(e) => setNamen((n) => ({ ...n, [s.label]: e.target.value }))} sx={{ width: 180 }} />
          ))}
          <Box component="button" type="button" disabled={bezig} onClick={bewaar}
            sx={{ ...stitchOutlineBtn, py: 0.4, px: 1.25, fontSize: '0.75rem' }}>Opslaan</Box>
        </Box>
      )}
      <Box sx={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {(data.alineas || []).map((a, i) => (
          <Typography key={i} variant="body2" sx={{ fontFamily: T.fontBody, maxWidth: '68ch' }}>
            <Box component="span" sx={{ color: T.secondary, fontVariantNumeric: 'tabular-nums', mr: 1 }}>{duurTekst(a.start)}</Box>
            {a.label != null && (
              <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>{a.naam || `Spreker ${a.label}`}:</Box>
            )}
            {a.tekst}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default function StartpakketClips({ job, open, onClose, onVernieuw }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [bezig, setBezig] = useState(false);
  const laad = useCallback(async () => {
    try { setData(await vraag(`/jobs/${job.id}/clips`)); } catch (e) { toast.error(e.message); }
  }, [job.id]);
  useEffect(() => { if (open) laad(); }, [open, laad]);
  const beheren = !!data?.rechten?.beheren;

  const doe = async (fn, gelukt) => {
    setBezig(true);
    try {
      const d = await fn();
      if (d?.clips) setData((oud) => ({ ...oud, ...d }));
      if (gelukt) toast.success(gelukt);
      await onVernieuw?.();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 700 }}>Clips en transcripten</DialogTitle>
      <DialogContent dividers>
        {!data && <Typography variant="body2" sx={{ color: T.outline }}>Laden…</Typography>}
        {data && !data.clips.length && (
          <Typography variant="body2" sx={{ color: T.secondary }}>Er zijn (nog) geen clips gelezen voor deze draaidag.</Typography>
        )}
        {data?.clips.map((c) => (
          <Box key={c.id} sx={{ py: 1, borderBottom: `1px solid ${T.surfaceLow}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere', flex: '1 1 220px' }}>
                {c.pad}
              </Typography>
              <Typography variant="caption" sx={{ color: T.secondary, fontVariantNumeric: 'tabular-nums' }}>
                {duurTekst(c.duurSec)}{c.vfr ? ' · VFR' : ''}
              </Typography>
              {beheren ? (
                <TextField select size="small" value={c.soort} disabled={bezig} sx={{ width: 140 }}
                  onChange={(e) => doe(() => post(`/jobs/${job.id}/clips/${c.id}/soort`, { soort: e.target.value }), 'Soort aangepast')}>
                  {Object.entries(SOORT_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
              ) : (
                <Typography variant="caption" sx={{ color: T.secondary }}>{SOORT_LABELS[c.soort] || c.soort}</Typography>
              )}
              {AUDIO_TEKST[c.audioStatus] && (
                <Typography variant="caption" sx={{ color: c.audioStatus === 'fout' ? T.error : T.outline }}>
                  {AUDIO_TEKST[c.audioStatus]}{c.audioFout === 'wacht_eu' ? ' (EU)' : ''}
                </Typography>
              )}
              {c.transcript && (
                <Box component="button" type="button" onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  sx={{ ...stitchOutlineBtn, py: 0.3, px: 1, fontSize: '0.75rem' }}>
                  <Icon name="subtitles" size={14} /> {openId === c.id ? 'Verberg' : 'Transcript'}
                </Box>
              )}
              {beheren && ['fout', 'overgeslagen'].includes(c.audioStatus) && ['interview', 'twijfel'].includes(c.soort) && (
                <Box component="button" type="button" disabled={bezig}
                  onClick={async () => {
                    const ok = await bevestig({ titel: 'Opnieuw transcriberen?', tekst: 'De NAS-brug maakt opnieuw een audiobestand van deze clip en de transcriptie start opnieuw (volgens het AI-beleid van de klant).', knop: 'Opnieuw' });
                    if (ok) doe(() => post(`/jobs/${job.id}/clips/${c.id}/opnieuw`), 'Wordt opnieuw getranscribeerd');
                  }}
                  sx={{ ...stitchOutlineBtn, py: 0.3, px: 1, fontSize: '0.75rem' }}>Opnieuw</Box>
              )}
            </Box>
            {openId === c.id && <Transcript jobId={job.id} clip={c} beheren={beheren} />}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Box component="button" type="button" onClick={onClose} sx={{ ...stitchOutlineBtn, py: 0.5, px: 1.5 }}>Sluiten</Box>
      </DialogActions>
    </Dialog>
  );
}
